import { ApolloLink, ApolloClient, InMemoryCache, ApolloProvider, defaultDataIdFromObject } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import fetch from 'isomorphic-fetch';
import * as React from 'react';
import { ErrorLink } from '@apollo/client/link/error';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import TimeoutLink from 'apollo-link-timeout';
import { RetryLink } from '@apollo/client/link/retry';
import { Sha256 } from '@aws-crypto/sha256-browser';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

var pageIdHandler = function (pageStore) { return function (operation, forward) {
    if (pageStore && pageStore.pageId) {
        operation.setContext(function (_a) {
            var headers = _a.headers;
            return ({
                headers: __assign(__assign({}, headers), { 'x-page-id': pageStore.pageId }),
            });
        });
    }
    return forward(operation);
}; };
var pageIdLink = function (pageStore) {
    return new ApolloLink(pageIdHandler(pageStore));
};

var GRAPHQL_ERROR_EVENT = new SystemEvent(SystemEventLevel.ERROR, 'APOLLO_GRAPHQL_ERROR');
var NETWORK_ERROR_EVENT = new SystemEvent(SystemEventLevel.ERROR, 'APOLLO_NETWORK_ERROR');
var errorHandler = function (logger, __EXPERIMENTAL__browser_reload_on_auth_error) {
    return function (_a) {
        var graphQLErrors = _a.graphQLErrors, networkError = _a.networkError, operation = _a.operation;
        var operationName = (operation && operation.operationName) || 'unknown';
        if (graphQLErrors) {
            graphQLErrors.forEach(function (error) {
                var message = error.message, extensions = error.extensions, locations = error.locations, path = error.path;
                logger.logEvent(GRAPHQL_ERROR_EVENT, message, {
                    operation: operationName,
                    locations: locations,
                    path: path,
                });
                if (typeof window !== 'undefined' &&
                    __EXPERIMENTAL__browser_reload_on_auth_error &&
                    (extensions === null || extensions === void 0 ? void 0 : extensions.code) === 'UNAUTHENTICATED') {
                    // designed to redirect the user to the login page when used in tandem
                    // with {@link https://github.expedia.biz/Catalyst/hapi-authorization hapi-authorization}
                    // and its redirect logic.
                    window.location.reload();
                }
            });
        }
        if (networkError) {
            logger.logEvent(NETWORK_ERROR_EVENT, networkError.message, { operation: operationName });
        }
    };
};
var errorLink = function (logger, __EXPERIMENTAL__browser_reload_on_auth_error) {
    return new ErrorLink(errorHandler(logger, __EXPERIMENTAL__browser_reload_on_auth_error));
};

var GRAPHQL_RETRY_EVENT = new SystemEvent(SystemEventLevel.INFO, 'GRAPHQL_RETRY_EVENT');
var GRAPHQL_MAX_RETRY_EVENTS_REACHED_EVENT = new SystemEvent(SystemEventLevel.ERROR, 'GRAPHQL_MAX_RETRY_EVENTS_REACHED');
var retryLinkOptions = function (logger, strategy) {
    var DEFAULT_INITIAL_DELAY = 300;
    var DEFAULT_MAX_RETRIES = 5;
    var initialDelay = strategy && strategy.initialDelay ? strategy.initialDelay : DEFAULT_INITIAL_DELAY;
    return {
        delay: {
            initial: initialDelay,
            max: Infinity,
            jitter: true,
        },
        attempts: function (count, operation, error) {
            var blacklist = strategy && strategy.blacklist ? strategy.blacklist : [];
            if (blacklist.includes(operation.operationName)) {
                return false;
            }
            var maxRetries = strategy && typeof strategy.maxRetries === 'number' ? strategy.maxRetries : DEFAULT_MAX_RETRIES;
            if (count < maxRetries) {
                logger.logEvent(GRAPHQL_RETRY_EVENT, "retry=".concat(count, " max_retry=").concat(maxRetries, " operation=").concat(operation.operationName));
            }
            else {
                logger.logEvent(GRAPHQL_MAX_RETRY_EVENTS_REACHED_EVENT, "max_retry=".concat(maxRetries, " operation=").concat(operation.operationName));
            }
            return !!error && count < maxRetries;
        },
    };
};
var retryLink = function (logger, strategy) {
    return new RetryLink(retryLinkOptions(logger, strategy));
};

/**
 * Takes in an array of PossibleTypesMap and merges them to a single object
 * @param possibleTypes PossibleTypesMap[]
 * @returns a single PossibleTypesMap
 */
var mergePossibleTypes = function (possibleTypes) {
    return possibleTypes.reduce(function (map, globOutput) {
        Object.keys(globOutput).forEach(function (key) {
            if (key in map) {
                map[key] = __spreadArray([], __read(new Set(map[key].concat(globOutput[key]))), false);
            }
            else {
                map[key] = globOutput[key];
            }
        });
        return map;
    }, {});
};

var sha256 = function (data) { return __awaiter(void 0, void 0, void 0, function () {
    var hash, result, hashArray, digest;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                hash = new Sha256();
                hash.update(data);
                return [4 /*yield*/, hash.digest()];
            case 1:
                result = _a.sent();
                hashArray = Array.from(result);
                digest = hashArray.map(function (b) { return b.toString(16).padStart(2, '0'); }).join('');
                return [2 /*return*/, digest];
        }
    });
}); };
function ApolloPlugin(pluginOptions) {
    if (pluginOptions === void 0) { pluginOptions = {}; }
    var browserTimeout = pluginOptions.browserTimeout, ssrForceFetchDelay = pluginOptions.ssrForceFetchDelay, retryStrategy = pluginOptions.retryStrategy, usePossibleTypes = pluginOptions.usePossibleTypes, __EXPERIMENTAL__browser_reload_on_auth_error = pluginOptions.__EXPERIMENTAL__browser_reload_on_auth_error, _a = pluginOptions.typePolicies, typePolicies = _a === void 0 ? {} : _a, _b = pluginOptions.possibleTypes, possibleTypes = _b === void 0 ? [] : _b, allowBrowserAPQRequests = pluginOptions.allowBrowserAPQRequests;
    var mappedPossibleTypes = usePossibleTypes ? mergePossibleTypes(possibleTypes) : {};
    return {
        name: 'apollo',
        onBrowserHydrateRenderContext: function (context, next) {
            var hydratedState = context.hydratedState, logger = context.logger, getAppData = context.getAppData, setAppData = context.setAppData;
            var apolloState = hydratedState.apolloState, clientInfo = hydratedState.clientInfo;
            var stores = getAppData().stores;
            var page = (stores && stores.page) || {};
            var batchLink = new BatchHttpLink({
                uri: "".concat(page.baseUrl || '', "/graphql"),
                headers: {
                    'Client-Info': clientInfo,
                },
                credentials: 'same-origin',
                batchKey: function (operation) { return operation.getContext().batchKey || operation.operationName; },
                fetch: fetch,
            });
            var apqLink = createPersistedQueryLink({ sha256: sha256 }).concat(batchLink);
            var terminatingLink = allowBrowserAPQRequests
                ? ApolloLink.split(function (op) { return op.getContext().useApq; }, apqLink, batchLink)
                : batchLink;
            var links = [
                errorLink(logger, __EXPERIMENTAL__browser_reload_on_auth_error),
                browserTimeout ? new TimeoutLink(browserTimeout) : null,
                retryStrategy ? retryLink(logger, retryStrategy) : null,
                pageIdLink(page),
                terminatingLink,
            ].filter(Boolean);
            var cacheOptions = {
                dataIdFromObject: function (responseObject) {
                    return defaultDataIdFromObject(responseObject);
                },
                typePolicies: typePolicies,
                possibleTypes: mappedPossibleTypes,
            };
            var apolloClient = new ApolloClient({
                link: ApolloLink.from(links),
                cache: new InMemoryCache(cacheOptions).restore(apolloState),
                resolvers: {},
                ssrForceFetchDelay: ssrForceFetchDelay,
            });
            setAppData({
                apollo: {
                    client: apolloClient,
                },
            });
            return next(context);
        },
        onBrowserWrapRootElement: function (context, next) {
            var getAppData = context.getAppData, setRootElement = context.setRootElement;
            var apollo = getAppData().apollo;
            var client = apollo.client;
            setRootElement(function (RootElement) { return function (props) { return (React.createElement(ApolloProvider, { client: client },
                React.createElement(RootElement, null, props.children))); }; });
            return next(context);
        },
    };
}

export { ApolloPlugin as default };
//# sourceMappingURL=browser.js.map
